import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { nameValidation } from 'validators';
import { BottomSheetContentLayout, Column, FunctionalLoader, Grid, GridItem, Menu, TextField, Typography, useIntentBehavior, useOverlayContext } from 'seagull';
import { Download, UserAdd } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { validateContact } from '~/business-logic/validators';
import { ErrorText } from '~/constants';
import { useEventReporter, useStore, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { UserRole } from '~/models';
import { selectors } from '~/store/state-selectors';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './InviteMemberBottomSheetFlow.module.scss';
const testPrefix = 'invite-member-bottom-sheet';
const defaultRole = UserRole.Member;
export const GetInviteMemberBottomSheetTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            InviteMemberBottomSheetTID: {
                DownloadPdf: 'cover-page-download-pdf',
                InviteMember: 'cover-page-invite-member',
                InputFirstName: 'invite-member-bottom-sheet-input-first-name',
                InputLastName: 'invite-member-bottom-sheet-input-last-name',
                InputEmail: 'invite-member-bottom-sheet-input-email',
                InputRole: 'invite-member-bottom-sheet-input-role',
                RoleOptOwner: 'invite-member-bottom-sheet-role-opt-owner',
                RoleOptMember: 'invite-member-bottom-sheet-role-opt-member',
                SendInvitation: 'invite-member-bottom-sheet-send-invitation'
            }
        }
    });
const ROLE_OPTIONS = [
    {
        value: UserRole.Owner
    },
    {
        value: UserRole.Member
    }
];
function InviteMemberRoleSelectionPage(param) {
    let { onSelect, disableOwner } = param;
    const { popBottomSheetPage } = useOverlayContext();
    const { t: inviteMemberTexts } = useLocaleKeys('clientFlow.inviteMember');
    const roleOptionTexts = {
        [UserRole.Owner]: {
            text: inviteMemberTexts.ownerText(),
            subtext: inviteMemberTexts.adminSubtext()
        },
        [UserRole.Member]: {
            text: inviteMemberTexts.memberText(),
            subtext: inviteMemberTexts.memberSubtext()
        }
    };
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentGutter: "m",
        header: {
            title: inviteMemberTexts.selectRole()
        },
        children: /*#__PURE__*/ _jsx(Menu, {
            classes: {
                root: styles.roleSelectionMenu
            },
            dimensions: {
                width: 'auto',
                paddingTop: 16
            },
            items: ROLE_OPTIONS.map((param)=>{
                let { value } = param;
                var _ref;
                var _roleOptionTexts_value, _roleOptionTexts_value1;
                return {
                    disabled: disableOwner && value === UserRole.Owner,
                    id: value,
                    title: (_ref = (_roleOptionTexts_value = roleOptionTexts[value]) === null || _roleOptionTexts_value === void 0 ? void 0 : _roleOptionTexts_value.text) !== null && _ref !== void 0 ? _ref : value,
                    body: (_roleOptionTexts_value1 = roleOptionTexts[value]) === null || _roleOptionTexts_value1 === void 0 ? void 0 : _roleOptionTexts_value1.subtext,
                    testId: "".concat(testPrefix, "-role-opt-").concat(value),
                    bi: {
                        name: 'invite_member_select_role',
                        data: {
                            role_id: value
                        }
                    },
                    onClick: ()=>{
                        onSelect(value);
                        popBottomSheetPage();
                    }
                };
            })
        })
    });
}
function InviteMemberFormPage(param) {
    let { onSuccess } = param;
    var _roleOptionTexts_selectedRole;
    const { t: inviteMemberTexts } = useLocaleKeys('clientFlow.inviteMember');
    const { pushBottomSheetPage, showErrorToast, showSuccessToast } = useOverlayContext();
    const worksAt = useStore(selectors.user.business);
    const userRole = useStore(selectors.user.role);
    const { setWorksAt, api, logBI } = useStoreActions();
    const { reportFormSubmission, prepareFormObject } = useEventReporter();
    const { control, formState, handleSubmit, setValue, watch, reset } = useForm({
        mode: 'all',
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            role: ''
        }
    });
    const [existingMembers, setExistingMembers] = useState();
    const firstNameValidator = useMemo(()=>nameValidation(inviteMemberTexts.firstName(), {
            required: inviteMemberTexts.firstNameRequired()
        }), []);
    const lastNameValidator = useMemo(()=>nameValidation(inviteMemberTexts.lastName(), {
            required: inviteMemberTexts.lastNameRequired()
        }), []);
    const roleValidator = useMemo(()=>nameValidation(inviteMemberTexts.role(), {
            required: inviteMemberTexts.roleRequired()
        }), []);
    const roleOptionTexts = {
        [UserRole.Owner]: inviteMemberTexts.ownerText(),
        [UserRole.Member]: inviteMemberTexts.memberText()
    };
    const selectedRole = watch('role');
    const roleLabel = selectedRole ? (_roleOptionTexts_selectedRole = roleOptionTexts[selectedRole]) !== null && _roleOptionTexts_selectedRole !== void 0 ? _roleOptionTexts_selectedRole : selectedRole : inviteMemberTexts.selectRole();
    useOnMount(()=>{
        async function retrieveMembers() {
            try {
                const { members } = await api.member.getAll();
                setExistingMembers(members);
            } catch (unused) {
                setExistingMembers([]);
            }
        }
        retrieveMembers();
    });
    const emailRules = ()=>({
            validate: {
                email: validateContact.email,
                emailAlreadyUsed: (email)=>(existingMembers === null || existingMembers === void 0 ? void 0 : existingMembers.every((m)=>m.email() !== email)) || inviteMemberTexts.emailAlreadyMemberError()
            }
        });
    const onValidSubmit = async (param)=>{
        let { firstName, lastName, email, role } = param;
        try {
            const invitedRole = role || defaultRole;
            logBI(12201, {
                email,
                role: invitedRole
            });
            await api.member.invite({
                firstName,
                lastName,
                email,
                role: invitedRole
            });
            if (worksAt) {
                setWorksAt(_object_spread_props(_object_spread({}, worksAt), {
                    membersCount: worksAt.membersCount + 1
                }));
            }
            reportFormSubmission('team-invited-member', [
                prepareFormObject('email', email),
                prepareFormObject('firstName', firstName),
                prepareFormObject('lastName', lastName)
            ]);
            onSuccess();
            showSuccessToast(inviteMemberTexts.invitationSent());
            reset();
        } catch (err) {
            const e = new ErrorResponse(err);
            const text = e.hasServerError(ServerError.PendingInvitationAlreadyExists) ? inviteMemberTexts.pendingInvitationError() : e.hasServerError([
                ServerError.UserAlreadyAffiliatedWithBusiness,
                ServerError.UserAlreadyInBusiness
            ]) ? inviteMemberTexts.emailAlreadyMemberError() : ErrorText.UnexpectedErrorTryAgain;
            showErrorToast(text);
        }
    };
    const openRoleSelection = ()=>{
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(InviteMemberRoleSelectionPage, {
                onSelect: (role)=>{
                    setValue('role', role, {
                        shouldValidate: true
                    });
                },
                disableOwner: userRole !== UserRole.Owner && userRole !== UserRole.AnchorAdmin
            })
        });
    };
    if (!existingMembers) {
        return /*#__PURE__*/ _jsx(FunctionalLoader, {
            show: true,
            name: "fetch-members-loader"
        });
    }
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentGutter: "m",
        header: {
            title: inviteMemberTexts.inviteTeamMember()
        },
        footer: {
            paddingTop: 8,
            primaryAction: {
                bi: {
                    name: 'send_team_member_invitation'
                },
                text: inviteMemberTexts.sendInvitation(),
                disabled: formState.isSubmitting,
                testId: "".concat(testPrefix, "-send-invitation"),
                onClick: handleSubmit(onValidSubmit)
            }
        },
        children: /*#__PURE__*/ _jsxs(Grid, {
            className: styles.formContent,
            children: [
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "firstName",
                        rules: firstNameValidator,
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                bi: {
                                    name: 'first_name'
                                },
                                collapseHelperText: false,
                                label: inviteMemberTexts.firstName(),
                                testId: "".concat(testPrefix, "-input-first-name")
                            }, connectControllerToTextField(renderProps), firstNameValidator.dataAttr))
                    })
                }),
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "lastName",
                        rules: lastNameValidator,
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                bi: {
                                    name: 'last_name'
                                },
                                collapseHelperText: false,
                                label: inviteMemberTexts.lastName(),
                                testId: "".concat(testPrefix, "-input-last-name")
                            }, connectControllerToTextField(renderProps), lastNameValidator.dataAttr))
                    })
                }),
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "email",
                        rules: emailRules(),
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                bi: {
                                    name: 'email'
                                },
                                collapseHelperText: false,
                                label: inviteMemberTexts.email(),
                                testId: "".concat(testPrefix, "-input-email")
                            }, connectControllerToTextField(renderProps)))
                    })
                }),
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "role",
                        rules: roleValidator,
                        render: (param)=>{
                            let { field, fieldState } = param;
                            var _fieldState_error;
                            return /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        className: styles.roleSelector,
                                        onClick: openRoleSelection,
                                        "data-testid": "".concat(testPrefix, "-input-role"),
                                        "data-error": fieldState.invalid ? 'true' : undefined,
                                        children: /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph1",
                                            skin: field.value ? undefined : 'secondary',
                                            weight: "normal",
                                            children: roleLabel
                                        })
                                    }),
                                    ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message) && /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph3",
                                        skin: "error",
                                        className: styles.errorText,
                                        children: fieldState.error.message
                                    })
                                ]
                            });
                        }
                    })
                })
            ]
        })
    });
}
export function InviteMemberActionsMenu(param) {
    let { onDownloadPdf } = param;
    const { pushBottomSheetPage, hideBottomSheet } = useOverlayContext();
    const { t: inviteMemberTexts } = useLocaleKeys('clientFlow.inviteMember');
    const { getDisabled } = useIntentBehavior();
    const isInviteDisabled = getDisabled(Intent.InviteTeamMember);
    const items = [
        {
            id: 'download-pdf',
            title: inviteMemberTexts.downloadPdf(),
            prefixIcon: /*#__PURE__*/ _jsx(Download, {}),
            testId: 'cover-page-download-pdf',
            bi: {
                name: 'cover_page_download_pdf'
            },
            onClick: ()=>{
                onDownloadPdf();
                hideBottomSheet();
            }
        },
        ...!isInviteDisabled ? [
            {
                id: 'invite-member',
                title: inviteMemberTexts.inviteTeamMember(),
                prefixIcon: /*#__PURE__*/ _jsx(UserAdd, {}),
                testId: 'cover-page-invite-member',
                bi: {
                    name: 'cover_page_invite_team_member'
                },
                onClick: ()=>{
                    pushBottomSheetPage({
                        content: /*#__PURE__*/ _jsx(InviteMemberFormPage, {
                            onSuccess: ()=>{}
                        })
                    });
                }
            }
        ] : []
    ];
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        children: /*#__PURE__*/ _jsx(Column, {
            className: styles.actionsMenu,
            children: /*#__PURE__*/ _jsx(Menu, {
                classes: {
                    root: styles.actionsMenuRoot
                },
                dimensions: {
                    width: 'auto',
                    paddingTop: 16
                },
                items: items
            })
        })
    });
}
